import { useQuery } from '@tanstack/vue-query';

export type NewsType = 'LAUNCH' | 'BROCHURE' | 'UPDATE' | 'INVENTORY' | 'INTEL' | 'HANDOVER';

export type NewsSourceChannel = 'WA' | 'TG' | 'CRON' | 'OTHER';

export type NewsSource = {
  channel: NewsSourceChannel;
  groupName: string | null;
  attachmentName: string | null;
  attachmentType: 'pdf' | 'image' | null;
  attachmentUrl: string | null;
};

export type NewsItem = {
  id: string;
  title: string;
  body: string;
  type: NewsType;
  publishedAt: string;
  developerId: number | null;
  developerName: string | null;
  developerLogoUrl: string | null;
  developerKey: string | null;
  projectId: number | null;
  projectName: string | null;
  projectGeniemapId: number | null;
  /** First relevance-filtered project image (logos/promos/mismatches excluded). */
  projectImageUrl: string | null;
  /** Full relevance-filtered image array for the card/detail carousel. */
  projectImages: string[];
  /** Project community/area. Null when unknown. */
  projectDistrict: string | null;
  /** Project handover (ISO string). Null when unknown. */
  projectHandover: string | null;
  /** Lowest unit price in AED. Null when unknown. */
  unitPriceFrom: number | null;
  /** Highest unit price in AED. Null when unknown. */
  unitPriceTo: number | null;
  source: NewsSource | null;
  isRead: boolean;
  isSaved: boolean;
  /** Published by the developer's verified representative (Developer Partner Network); id is "dn-<id>". */
  developerConfirmed?: boolean;
};

export type NewsListResponse = {
  items: NewsItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type NewsListParams = {
  page?: number;
  pageSize?: number;
  /** CSV-encoded categories (LAUNCH/BROCHURE/UPDATE/INVENTORY/INTEL/HANDOVER). */
  type?: NewsType | NewsType[];
  /** Single developer id or array of bigints (CSV-encoded server-side). */
  developerId?: number | number[];
  /**
   * Free-form developer name (e.g. Geniemap `developer.title.en`). Server
   * resolves it to bot developer IDs via `normalizeDeveloperName`. Use this
   * when the caller only knows the display name (project pages, etc.).
   */
  developerName?: string;
  projectId?: number;
  search?: string;
  saved?: boolean;
  /**
   * Server-side dedup. Defaults to ON (server-side); pass `false` to opt out
   * and receive raw activity history (e.g. for admin/debug views).
   */
  dedup?: boolean;
};

export const useNewsListQuery = (params: MaybeRef<NewsListParams> = {}) => {
  const authStore = useAuthStore();
  const paramsRef = computed(() => unref(params) ?? {});

  const buildQueryString = () => {
    const p = paramsRef.value;
    const search = new URLSearchParams();
    if (p.page) search.set('page', String(p.page));
    if (p.pageSize) search.set('pageSize', String(p.pageSize));
    if (p.type) {
      const csv = Array.isArray(p.type) ? p.type.join(',') : p.type;
      if (csv) search.set('type', csv);
    }
    if (p.developerId !== undefined) {
      const csv = Array.isArray(p.developerId) ? p.developerId.join(',') : String(p.developerId);
      if (csv) search.set('developerId', csv);
    }
    if (p.developerName && p.developerName.trim()) {
      search.set('developerName', p.developerName.trim());
    }
    if (typeof p.projectId === 'number') search.set('projectId', String(p.projectId));
    if (p.search) search.set('search', p.search);
    if (p.saved) search.set('saved', 'true');
    if (p.dedup === false) search.set('dedup', 'false');
    return search.toString();
  };

  return useQuery({
    queryKey: computed(() => ['news', 'list', paramsRef.value]),
    queryFn: () => {
      const qs = buildQueryString();
      return $fetch<{ success: boolean; data: NewsListResponse }>(`/api/news${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Bearer ${authStore.token}` },
      });
    },
    enabled: computed(() => !!authStore.token),
    select: (res) => res?.data ?? null,
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
};
